import React, { useRef } from "react";
import { PortalID } from "frontend/modal/portal-types";
import { CursorType } from "../cursor-type";
import style from "./card-stack-submenu.module.css";
import { Floater } from "frontend/ui-components/floaters/floater";
import ReactDOM from "react-dom";
import { CardStackIcon } from "frontend/icons/card-stack";
import { LiveCardStackIcon } from "frontend/icons/live-card-stack";
import classNames from "classnames";
import { useIsCIMReorderEnabled } from "utils/url-utils";
import { useFloating, autoUpdate, offset, flip, shift, FloatingPortal, arrow, FloatingArrow } from "@floating-ui/react";

export default function CardStackSubMenu({
  selectedType,
  onTypeClicked,
  relativeTo,
  onHover,
  onLeave,
}: {
  selectedType: CursorType;
  onTypeClicked: (type: CursorType) => void;
  relativeTo: React.RefObject<HTMLElement>;
  onHover?: () => void;
  onLeave?: () => void;
}) {
  const portal = document.getElementById(PortalID.Floaters);
  const isCimReorderEnabled = useIsCIMReorderEnabled();
  const arrowRef = useRef(null);

  // Set up floating UI
  const { refs, floatingStyles, context } = useFloating({
    open: isCimReorderEnabled && !!relativeTo.current,
    placement: "top",
    middleware: [offset(15), flip(), shift({ padding: 5 }), arrow({ element: arrowRef })],
    whileElementsMounted: autoUpdate,
  });

  // Set the reference element when it changes
  React.useEffect(() => {
    if (relativeTo.current) {
      refs.setReference(relativeTo.current);
    }
  }, [refs, relativeTo]);

  const items = [
    {
      iconRenderer: () => <CardStackIcon width={13} height={13} />,
      onClick: () => onTypeClicked(CursorType.cardStack),
      type: CursorType.cardStack,
      title: "Task stack",
    },
    {
      iconRenderer: () => <LiveCardStackIcon width={13} height={13} />,
      onClick: () => onTypeClicked(CursorType.liveIntegration),
      type: CursorType.liveIntegration,
      title: "Synced stack",
    },
  ];

  // Render content function
  const renderContent = () => (
    <>
      {items.map((item) => (
        <div
          key={item.type}
          className={classNames(style.item, { [style.selected]: selectedType === item.type })}
          onClick={item.onClick}
        >
          <div className={style.iconContainer}>{item.iconRenderer()}</div>
          <div className={style.title}>{item.title}</div>
        </div>
      ))}
    </>
  );

  // When CIM footer is enabled, use FloatingPortal
  if (isCimReorderEnabled && relativeTo.current) {
    return (
      <FloatingPortal id={PortalID.Floaters}>
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          className={classNames(style.container, style.containerCIMFooterEnabled)}
          onMouseEnter={onHover}
          onMouseLeave={onLeave}
        >
          {renderContent()}
          <FloatingArrow ref={arrowRef} context={context} fill="white" width={14} height={7} />
        </div>
      </FloatingPortal>
    );
  }

  // Original logic for non-CIM footer
  const element = relativeTo.current ? (
    <>
      <Floater relativeTo={relativeTo} side={"right"}>
        <div className={style.container} onMouseOver={onHover} onMouseEnter={onHover} onMouseLeave={onLeave}>
          {renderContent()}
        </div>
      </Floater>
      <Floater
        relativeTo={relativeTo}
        side={"right"}
        extraStyle={{
          transition: "opacity 0.1s ease-out, transform 0.1s ease-out",
          marginLeft: "-6px",
        }}
      >
        <div className={style.arrow} />
      </Floater>
    </>
  ) : null;

  return portal ? ReactDOM.createPortal(element, portal) : element;
}
